/**
 * Dynamic Parameterization Engine (Client-Side Async Pipeline & State Sync)
 */

export type EntityValue = number | string | boolean | null;

export type OperatorType = "AND" | "OR";

export interface Condition {
  fieldId: string;
  operator: string;
  value: any;
}

export interface PremiseGroup {
  operator: OperatorType;
  conditions: Condition[];
}

export type MathOperation = "ADD" | "SUBTRACT" | "MULTIPLY" | "DIVIDE" | "EQUALS" | "NOT_EQUALS" | "GREATER_THAN" | "LESS_THAN" | "GREATER_THAN_EQUALS" | "LESS_THAN_EQUALS" | "AND" | "OR";

export interface Calculation {
  type: "FIELD" | "CONSTANT" | "OPERATION" | "FUNCTION" | "THIS_ROW";
  value?: string | number; // fieldId, constant value, or function name
  operation?: MathOperation;
  left?: Calculation;
  right?: Calculation;
  args?: Calculation[]; // for functions
}

export interface ContainerDef {
  id: string;
  name: string;
  targetForm?: string;
  premises: PremiseGroup;
  returnValue: Calculation | { type: "FIELD" | "CONSTANT"; value: any };
  fallbackValue: any;
}

// Helper functions for Time/Date
const timeRegex = /^-?\d{2,}:\d{2}(:\d{2})?$/;

export function extractTime(datetimeStr: any): string | null {
    if (!datetimeStr) return null;

    if (typeof datetimeStr === 'number' || (typeof datetimeStr === 'string' && !isNaN(Number(datetimeStr)) && datetimeStr.trim() !== '')) {
        const d = new Date(Number(datetimeStr));
        if (!isNaN(d.getTime())) {
            const h = String(d.getHours()).padStart(2, '0');
            const m = String(d.getMinutes()).padStart(2, '0');
            const s = String(d.getSeconds()).padStart(2, '0');
            return `${h}:${m}:${s}`;
        }
    }

    const str = String(datetimeStr).trim();
    const match = str.match(/(?:^|\s|T)(\d{2}:\d{2}(:\d{2})?)/);
    return match ? match[1] : null;
}

export function extractDate(datetimeStr: any): string | null {
    if (!datetimeStr) return null;

    if (typeof datetimeStr === 'number' || (typeof datetimeStr === 'string' && !isNaN(Number(datetimeStr)) && datetimeStr.trim() !== '')) {
        const d = new Date(Number(datetimeStr));
        if (!isNaN(d.getTime())) {
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        }
    }

    const str = String(datetimeStr).trim();
    let match = str.match(/(?:^|\s)(\d{2}\/\d{2}\/\d{4})(?:\s|$|T)/);
    if (match) return match[1];

    match = str.match(/(?:^|\s)(\d{4}-\d{2}-\d{2})(?:T|\s|$)/);
    if (match) {
        const parts = match[1].split('-');
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    return null;
}

export function isTimeString(val: any): boolean {
    if (typeof val !== 'string') return false;
    return timeRegex.test(val.trim());
}

export function timeToSeconds(timeStr: string): number {
    const str = timeStr.trim();
    const isNegative = str.startsWith('-');
    const parts = str.replace('-', '').split(':').map(Number);
    let seconds = 0;
    if (parts.length >= 2) {
        seconds += parts[0] * 3600; // Hours
        seconds += parts[1] * 60; // Minutes
        if (parts.length === 3 && !isNaN(parts[2])) {
            seconds += parts[2];
        }
    }
    return isNegative ? -seconds : seconds;
}

export function secondsToTime(totalSeconds: number): string {
    const isNegative = totalSeconds < 0;
    const absSeconds = Math.abs(Math.round(totalSeconds));
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const seconds = absSeconds % 60;

    const hStr = String(hours).padStart(2, '0');
    const mStr = String(minutes).padStart(2, '0');
    const sStr = String(seconds).padStart(2, '0');

    return `${isNegative ? '-' : ''}${hStr}:${mStr}:${sStr}`;
}

// RN06: Ordenação Topológica Real (DAG Checker)
export function sortContainersTopologically(containers: ContainerDef[]): ContainerDef[] {
  const graph = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  for (const c of containers) {
    if (!graph.has(c.id)) {
      graph.set(c.id, []);
      inDegree.set(c.id, 0);
    }
  }

  const getDeps = (c: ContainerDef): string[] => {
    const deps = new Set<string>();
    const scanPremises = (p: PremiseGroup) => {
      for (const cond of p.conditions) {
        const fId = String(cond.fieldId);
        if (fId.startsWith('c_param_')) {
          deps.add(fId.replace('c_', ''));
        } else if (fId.startsWith('param_')) {
          deps.add(fId);
        }
      }
    };
    scanPremises(c.premises);

    const scanCalc = (calc: Calculation) => {
      if (calc.type === "FIELD" || calc.type === "THIS_ROW") {
        const valStr = String(calc.value);
        if (valStr.startsWith('c_param_')) {
          deps.add(valStr.replace('c_', ''));
        } else if (valStr.startsWith('param_')) {
          deps.add(valStr);
        }
      }
      if (calc.left) scanCalc(calc.left);
      if (calc.right) scanCalc(calc.right);
      if (calc.args) {
          // Aprofunda a varredura dentro dos argumentos (ex: FILTER conditions)
          calc.args.forEach(arg => scanCalc(arg));
      }
    };

    if (c.returnValue && c.returnValue.type && c.returnValue.type !== "CONSTANT") {
      scanCalc(c.returnValue as Calculation);
    }

    return Array.from(deps).filter(id => graph.has(id));
  };

  for (const c of containers) {
    const deps = getDeps(c);
    for (const dep of deps) {
      if (graph.has(dep)) {
        graph.get(dep)!.push(c.id);
        inDegree.set(c.id, inDegree.get(c.id)! + 1);
      }
    }
  }

  const queue: string[] = [];
  for (const [id, deg] of inDegree.entries()) {
    if (deg === 0) queue.push(id);
  }

  const sorted: ContainerDef[] = [];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    const cObj = containers.find(c => c.id === curr);
    if (cObj) sorted.push(cObj);
    for (const neighbor of (graph.get(curr) || [])) {
      inDegree.set(neighbor, inDegree.get(neighbor)! - 1);
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor);
      }
    }
  }

  // Se o len do array sorteado for menor que a entrada, há um loop que o front-end deixou passar
  if (sorted.length !== containers.length) {
    throw new Error("Loop Infinito Detectado (DAG). Impossível resolver a equação.");
  }

  return sorted;
}

// RN11: Null-Safety Operations
export function safeCalculate(calc: Calculation, rowData: Record<string, any>): any | null {
  if (calc.type === "CONSTANT") {
      if (typeof calc.value === 'string') {
          const val = calc.value;
          // Backwards compatibility for old ASTs
          if ((val.startsWith("'") && val.endsWith("'")) || 
              (val.startsWith('"') && val.endsWith('"')) || 
              (val.startsWith('`') && val.endsWith('`'))) {
              return val.slice(1, -1);
          }
          return val; // It's a string, keep it as a string
      }
      return isNaN(Number(calc.value)) ? calc.value : Number(calc.value);
  }
  if (calc.type === "FIELD") {
    const fieldName = String(calc.value);
    let reqFormId: string | null = null;
    let reqFieldId = fieldName;
    if (fieldName.includes('::')) {
        [reqFormId, reqFieldId] = fieldName.split('::');
    }
    if (reqFormId && reqFormId !== 'global') {
        const rowFormId = rowData['meta_form_id'] || rowData['formId'] || rowData['form_id'];
        if (rowFormId !== reqFormId) return null;
    }
    const val = rowData[reqFieldId];
    if (val === undefined || val === null || val === "") return null;
    if (typeof val === 'string') {
        if (val.length > 1 && val.startsWith('0') && !val.includes('.')) return val;
    }
    return isNaN(Number(val)) ? val : Number(val);
  }

  if (calc.type === "THIS_ROW") {
    const fieldName = String(calc.value);
    let reqFormId: string | null = null;
    let reqFieldId = fieldName;
    if (fieldName.includes('::')) {
        [reqFormId, reqFieldId] = fieldName.split('::');
    }
    if (reqFormId && reqFormId !== 'global') {
        const rowFormId = rowData['meta_form_id'] || rowData['formId'] || rowData['form_id'];
        if (rowFormId !== reqFormId) return null;
    }
    const val = rowData[reqFieldId];
    if (val === undefined || val === null || val === "") return null;
    if (typeof val === 'string') {
        if (val.length > 1 && val.startsWith('0') && !val.includes('.')) return val;
    }
    return isNaN(Number(val)) ? val : Number(val);
  }

  if (calc.type === "FUNCTION" && calc.args && calc.args.length > 0) {
    const funcName = String(calc.value).toUpperCase();

    if (funcName === "SWITCH") {
        const targetVal = safeCalculate(calc.args[0], rowData);
        for (let i = 1; i < calc.args.length - 1; i += 2) {
            const caseVal = safeCalculate(calc.args[i], rowData);
            
            const isTargetNumericStr = typeof targetVal === 'string' && targetVal.trim() !== '' && !isNaN(Number(targetVal));
            const isCaseNumericStr = typeof caseVal === 'string' && caseVal.trim() !== '' && !isNaN(Number(caseVal));
            const isTargetNum = typeof targetVal === 'number';
            const isCaseNum = typeof caseVal === 'number';
            const numericMatch = (isTargetNumericStr || isTargetNum) && (isCaseNumericStr || isCaseNum) && Number(targetVal) === Number(caseVal);
            
            const strMatch = typeof targetVal === 'string' && typeof caseVal === 'string' && targetVal.trim().toUpperCase() === caseVal.trim().toUpperCase();
            
            if (targetVal === caseVal || String(targetVal) === String(caseVal) || numericMatch || strMatch) {
                return safeCalculate(calc.args[i+1], rowData);
            }
        }
        if (calc.args.length % 2 === 0) {
            return safeCalculate(calc.args[calc.args.length - 1], rowData);
        }
        return null;
    }

    if (funcName === "TIME") {
        return extractTime(safeCalculate(calc.args[0], rowData));
    }
    if (funcName === "DATE") {
        return extractDate(safeCalculate(calc.args[0], rowData));
    }
    if (funcName === "HORAS") {
        const val = safeCalculate(calc.args[0], rowData);
        if (isTimeString(val)) return timeToSeconds(val as string) / 3600;
        return null;
    }
    if (funcName === "MINUTOS") {
        const val = safeCalculate(calc.args[0], rowData);
        if (isTimeString(val)) return timeToSeconds(val as string) / 60;
        return null;
    }
    if (funcName === "LATLONG" && calc.args.length === 2) {
        const lat = safeCalculate(calc.args[0], rowData);
        const lng = safeCalculate(calc.args[1], rowData);
        if (lat !== null && lng !== null) return `${lat},${lng}`;
        return null;
    }

    if (funcName === "FILTER" && calc.args.length === 2) { console.log(`[DEBUG FILTER] eval`);
      // In single cell context, FILTER doesn't make much sense unless it operates on a dataset.
      // But for null safety check, we'll just evaluate the calculation part (arg 1)
      return safeCalculate(calc.args[1], rowData);
    }

    const argVal = safeCalculate(calc.args[0], rowData);
    // Para simplificar, assumimos que arrays viriam se a row permitisse, mas como avaliamos
    // linha a linha, funções retornam o valor limpo em processamentos single-cell.
    return argVal;
  }

  if (calc.type === "OPERATION" && calc.left && calc.right) {
    const leftVal = safeCalculate(calc.left, rowData);
    const rightVal = safeCalculate(calc.right, rowData);

    // Matemática contra NULL resulta em NULL
    if (leftVal === null || rightVal === null) return null;

    const isMathOp = ["ADD", "SUBTRACT", "MULTIPLY", "DIVIDE"].includes(calc.operation);
    if (isMathOp) { console.log(`[DEBUG MathOp] op=${calc.operation} left=${leftVal} right=${rightVal} isTime(L)=${isTimeString(leftVal)} isTime(R)=${isTimeString(rightVal)}`);
       if (!isTimeString(leftVal) && isNaN(Number(leftVal))) return "[DADO INVÁLIDO]";
       if (!isTimeString(rightVal) && isNaN(Number(rightVal))) return "[DADO INVÁLIDO]";
    }

    switch (calc.operation) {
      case "ADD": {
          if (isTimeString(leftVal) && isTimeString(rightVal)) {
              return secondsToTime(timeToSeconds(leftVal as string) + timeToSeconds(rightVal as string));
          }
          return Number(leftVal) + Number(rightVal);
      }
      case "SUBTRACT": {
          if (isTimeString(leftVal) && isTimeString(rightVal)) {
              return secondsToTime(timeToSeconds(leftVal as string) - timeToSeconds(rightVal as string));
          }
          return Number(leftVal) - Number(rightVal);
      }
      case "MULTIPLY": return Number(leftVal) * Number(rightVal);
      case "DIVIDE":
        if (Number(rightVal) === 0) return "[ERRO DE CÁLCULO]"; // Divisão por zero resulta em ERRO
        return Number(leftVal) / Number(rightVal);
      case "EQUALS": {
        const lStr = Array.isArray(leftVal) ? JSON.stringify(leftVal) : String(leftVal);
        const rStr = Array.isArray(rightVal) ? JSON.stringify(rightVal) : String(rightVal);
        if (leftVal === rightVal || lStr === rStr) return 1;
        const isLeftNum = (typeof leftVal === 'number') || (typeof leftVal === 'string' && leftVal.trim() !== '' && !isNaN(Number(leftVal)));
        const isRightNum = (typeof rightVal === 'number') || (typeof rightVal === 'string' && rightVal.trim() !== '' && !isNaN(Number(rightVal)));
        if (isLeftNum && isRightNum && Number(leftVal) === Number(rightVal)) return 1;
        return 0;
      }
      case "NOT_EQUALS": {
        const lStr = Array.isArray(leftVal) ? JSON.stringify(leftVal) : String(leftVal);
        const rStr = Array.isArray(rightVal) ? JSON.stringify(rightVal) : String(rightVal);
        if (leftVal === rightVal || lStr === rStr) return 0;
        const isLeftNum = (typeof leftVal === 'number') || (typeof leftVal === 'string' && leftVal.trim() !== '' && !isNaN(Number(leftVal)));
        const isRightNum = (typeof rightVal === 'number') || (typeof rightVal === 'string' && rightVal.trim() !== '' && !isNaN(Number(rightVal)));
        if (isLeftNum && isRightNum && Number(leftVal) === Number(rightVal)) return 0;
        return 1;
      }
      case "GREATER_THAN": return leftVal > rightVal ? 1 : 0;
      case "LESS_THAN": return leftVal < rightVal ? 1 : 0;
      case "GREATER_THAN_EQUALS": return leftVal >= rightVal ? 1 : 0;
      case "LESS_THAN_EQUALS": return leftVal <= rightVal ? 1 : 0;
      case "AND": return (leftVal !== 0 && rightVal !== 0) ? 1 : 0;
      case "OR": return (leftVal !== 0 || rightVal !== 0) ? 1 : 0;
      default: return null;
    }
  }
  return null;
}

export function evaluateCondition(cond: Condition, rowData: Record<string, any>): boolean {
  let reqFormId: string | null = null;
  let reqFieldId = String(cond.fieldId);
  if (reqFieldId.includes('::')) {
      [reqFormId, reqFieldId] = reqFieldId.split('::');
  }
  if (reqFormId && reqFormId !== 'global') {
      const rowFormId = rowData['meta_form_id'] || rowData['formId'] || rowData['form_id'];
      if (rowFormId !== reqFormId) return false;
  }

  const cellValue = rowData[reqFieldId];

  // Zonas cegas e Paradoxo do Vazio
  const isEmpty = cellValue === null || cellValue === undefined || cellValue === "";
  if (isEmpty) {
    // Para não quebrar lógicas de exclusão indevidamente, avaliamos NOT_EQUALS e NOT_CONTAINS como true frente ao vazio.
    // Qualquer verificação positiva (EQUALS, GREATER, LESS, CONTAINS) falha contra o vazio.
    if (cond.operator === "NOT_EQUALS" || cond.operator === "neq") return true;
    if (cond.operator === "NOT_CONTAINS" || cond.operator === "not_contains") return true;
    return false;
  }

  switch (cond.operator) {
    case "EQUALS":
    case "eq": return cellValue == cond.value;
    case "NOT_EQUALS":
    case "neq": return cellValue != cond.value;
    case "GREATER_THAN":
    case "gt": return Number(cellValue) > Number(cond.value);
    case "LESS_THAN":
    case "lt": return Number(cellValue) < Number(cond.value);
    case "CONTAINS":
    case "contains": return String(cellValue).includes(String(cond.value));
    case "NOT_CONTAINS":
    case "not_contains": return !String(cellValue).includes(String(cond.value));
    default: return false;
  }
}

export function evaluatePremises(group: PremiseGroup, rowData: Record<string, any>): boolean {
  if (!group.conditions || group.conditions.length === 0) return true;

  if (group.operator === "AND") {
    return group.conditions.every(c => evaluateCondition(c, rowData));
  } else {
    return group.conditions.some(c => evaluateCondition(c, rowData));
  }
}

export function processContainerForRow(container: ContainerDef, rowData: Record<string, any>): any | null {
  const meetsPremise = evaluatePremises(container.premises, rowData);
  if (meetsPremise) {
    if (container.returnValue.type === "CONSTANT" || container.returnValue.type === "FIELD") {
      if (container.returnValue.type === "FIELD") {
        const fieldName = String(container.returnValue.value);
        let reqFormId: string | null = null;
        let reqFieldId = fieldName;
        if (fieldName.includes('::')) {
            [reqFormId, reqFieldId] = fieldName.split('::');
        }
        if (reqFormId && reqFormId !== 'global') {
            const rowFormId = rowData['meta_form_id'] || rowData['formId'] || rowData['form_id'];
            if (rowFormId !== reqFormId) return null;
        }
        const val = rowData[reqFieldId];
        return (val === undefined || val === "") ? null : val;
      }
      return container.returnValue.value;
    }

    return safeCalculate(container.returnValue as Calculation, rowData);
  }

  return null; // Return NULL instead of fallback for internal calc
}

// RN08 & RN07: Pipeline Assíncrono (Chunking)
export async function processParametrizationPipeline(
  containers: ContainerDef[],
  dataset: Record<string, any>[],
  chunkSize: number = 500,
  onProgress?: (progress: number) => void
): Promise<Record<string, any>[]> {
  // ORDENAÇÃO TOPOLÓGICA (Resolve a Vulnerabilidade 17.3 e Cenário H)
  const sortedContainers = sortContainersTopologically(containers);

  // Previne colapso de memória via alocação de array ao invés de deep clone maciço (Spread [...dataset])
  const results = new Array(dataset.length);
  let processed = 0;

  for (let i = 0; i < dataset.length; i += chunkSize) {
    const chunkEnd = Math.min(i + chunkSize, dataset.length);

    for (let rowIdx = i; rowIdx < chunkEnd; rowIdx++) {
      // Clonagem isolada apenas da linha em processo
      const row = { ...dataset[rowIdx] };

      for (const container of sortedContainers) {
        const result = processContainerForRow(container, row);
        row[`c_${container.id}`] = result;
        row[container.id] = result;
      }

      results[rowIdx] = row;
      processed++;
    }

    if (onProgress) {
      onProgress(Math.floor((processed / dataset.length) * 100));
    }

    // Pause microscópico para não travar a UI Thread (RN07)
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return results;
}

// RN11: Quantitativos e Agregações (Segurança Matemática)
export function aggregateResults(dataset: Record<string, any>[], columnId: string, aggType: "SUM" | "AVG" | "MIN" | "MAX"): number | null {
  let validValues: number[] = [];

  for (const row of dataset) {
    const val = row[columnId];
    if (val !== null && val !== undefined && val !== "" && !isNaN(Number(val))) {
      validValues.push(Number(val));
    }
  }

  if (validValues.length === 0) {
      if (aggType === "SUM") return 0;
      return null;
  }

  switch(aggType) {
    case "SUM": return validValues.reduce((a,b) => a + b, 0);
    case "AVG": return validValues.reduce((a,b) => a + b, 0) / validValues.length;
    case "MIN": return Math.min(...validValues);
    case "MAX": return Math.max(...validValues);
  }
  return null;
}

// ETAPA 1: O Motor de Expressões (Compostas por Agregação)
export function evaluateParameterFormula(
  dataset: Record<string, any>[],
  groupKey: string,
  parameterSchema: ContainerDef
): { group: string; value: number | null }[] {

  // 1. Group data
  const groups: Record<string, Record<string, any>[]> = {};

  for (const row of dataset) {
    const keyVal = row[groupKey];
    const groupName = keyVal !== undefined && keyVal !== null && keyVal !== "" ? String(keyVal) : "Desconhecido";
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(row);
  }

  // 2. Evaluate expression for a specific dataset
  const evalCalcOnGroup = (calc: Calculation, groupData: Record<string, any>[]): number | null => {
    if (calc.type === "CONSTANT") return Number(calc.value);

    // Aggregation Function
    if (calc.type === "FUNCTION" && calc.value && calc.args && calc.args.length > 0) {
       const fieldCalc = calc.args[0];
       if (fieldCalc.type !== "FIELD") return null; // Simplified: functions expect a field to aggregate

       const aggType = String(calc.value).toUpperCase() as "SUM" | "AVG" | "MIN" | "MAX";
       if (!["SUM", "AVG", "MIN", "MAX"].includes(aggType)) return null;

       return aggregateResults(groupData, String(fieldCalc.value), aggType);
    }

    // Fallback if someone references a FIELD outside an aggregation in a grouped context
    // (technically not valid for grouped charts, but we take the first row or SUM as fallback if needed)
    if (calc.type === "FIELD") {
       return aggregateResults(groupData, String(calc.value), "SUM");
    }

    if (calc.type === "OPERATION" && calc.left && calc.right) {
      const leftVal = evalCalcOnGroup(calc.left, groupData);
      const rightVal = evalCalcOnGroup(calc.right, groupData);

      if (leftVal === null || rightVal === null) return null;

      switch (calc.operation) {
        case "ADD": return leftVal + rightVal;
        case "SUBTRACT": return leftVal - rightVal;
        case "MULTIPLY": return leftVal * rightVal;
        case "DIVIDE":
          if (rightVal === 0) return null; // NaN/Infinity guard
          return leftVal / rightVal;
        default: return null;
      }
    }

    return null;
  };

  // 3. Map Groups to Values
  const results: { group: string; value: number | null }[] = [];

  for (const [groupName, groupData] of Object.entries(groups)) {
    // Only process rows that meet premises
    const filteredGroup = groupData.filter(row => evaluatePremises(parameterSchema.premises, row));

    if (filteredGroup.length === 0) {
      results.push({ group: groupName, value: null });
      continue;
    }

    let groupValue: number | null = null;

    if (parameterSchema.returnValue.type === "CONSTANT") {
       groupValue = Number(parameterSchema.returnValue.value);
    } else if (parameterSchema.returnValue.type === "FIELD") {
       groupValue = aggregateResults(filteredGroup, String(parameterSchema.returnValue.value), "SUM");
    } else {
       groupValue = evalCalcOnGroup(parameterSchema.returnValue as Calculation, filteredGroup);
    }

    // Apply fallback if null
    if (groupValue === null && parameterSchema.fallbackValue !== undefined && parameterSchema.fallbackValue !== "") {
       groupValue = Number(parameterSchema.fallbackValue);
    }

    results.push({ group: groupName, value: groupValue });
  }

  return results;
}

// PROTOCOLO 9.0: "Two-Pass Evaluator" - Motor Híbrido
export function injectDynamicParameters(dataset: any[], parameters: ContainerDef[]): any[] {
  let globalAliases: { id: string, name: string, linkedFieldIds: string[] }[] = [];
  try {
     if (typeof window !== 'undefined' && window.localStorage) {
        const str = localStorage.getItem('insights-global-aliases');
        if (str) globalAliases = JSON.parse(str);
     }
  } catch(e) {}

  let globalInputs: { id: string, name: string, type: string, value: any }[] = [];
  try {
     if (typeof window !== 'undefined' && window.localStorage) {
        const str = localStorage.getItem('insights-global-inputs');
        if (str) globalInputs = JSON.parse(str);
     }
  } catch(e) {}

  if ((!parameters || parameters.length === 0) && globalAliases.length === 0 && globalInputs.length === 0) return dataset;
  if (dataset.length === 0) return dataset;

  // PASSAGEM 1: Pré-Computar todos os nós de agregação (FUNCTION) para todos os parâmetros.
  // A cache indexará o resultado global das agregações para o dataset atual.
  // Como as agregações olham para o dataset global (ex: "Soma de todos os litros abastecidos filtrados"),
  // o resultado delas é estático para esta iteração do dataset, EXCETO para as funções FILTER
  // que precisam ser avaliadas no contexto de cada linha.
  const globalAggCache = new Map<string, number | null>(); // key: paramId_aggregationHash

  const computeAggregationForDataset = (aggType: string, fieldName: string, targetDataset: any[]): number | null => {
    let validValues: number[] = [];
    
    let reqFormId: string | null = null;
    let reqFieldId = fieldName;
    if (fieldName.includes('::')) {
       [reqFormId, reqFieldId] = fieldName.split('::');
    }

    for (const row of targetDataset) {
      if (reqFormId && reqFormId !== 'global') {
          const rowFormId = row.formId || row.form_id;
          if (rowFormId !== reqFormId) continue;
      }

      let val = row[reqFieldId];
      if (val === undefined && row.data && row.data[reqFieldId]) {
        val = row.data[reqFieldId].value;
      }
      if (val !== undefined && val !== null && val !== "" && !isNaN(Number(val))) {
        validValues.push(Number(val));
      }
    }

    let result: number | null = null;
    if (validValues.length > 0) {
      switch (aggType) {
        case "SUM": result = validValues.reduce((a, b) => a + b, 0); break;
        case "AVG": result = validValues.reduce((a, b) => a + b, 0) / validValues.length; break;
        case "MIN": result = Math.min(...validValues); break;
        case "MAX": result = Math.max(...validValues); break;
      }
    } else {
        if (aggType === "SUM") result = 0;
    }
    return result;
  };

  const computeAggregations = (calc: Calculation, paramId: string) => {
    if (calc.type === 'FUNCTION' && calc.value && calc.args && calc.args.length > 0) {
      const funcName = String(calc.value).toUpperCase();

      if (funcName === "FILTER") {
        // FILTER is handled dynamically during row evaluation because it can depend on THIS_ROW
      } else {
        const fieldCalc = calc.args[0];
        if (fieldCalc.type === 'FIELD') {
          const aggType = funcName as "SUM" | "AVG" | "MIN" | "MAX";
          const fieldName = String(fieldCalc.value);
          const cacheKey = `${paramId}_${aggType}_${fieldName}`;

          if (!globalAggCache.has(cacheKey)) {
            // Fix: Enforce dataset isolation for global aggregations based on targetForm
            const paramDefinition = parameters.find(p => p.id === paramId);
            let tForm = paramDefinition?.targetForm || (paramDefinition as any)?._metadata?.targetForm;
            if (tForm === 'form-2') tForm = 'form-1775766905782'; // Legacy migration
            if (tForm === 'form-1') tForm = 'form-1775762620276';

            const targetDataset = dataset.filter(r => {
                if (tForm && tForm !== 'global' && (r.formId || r.form_id) !== tForm) return false;
                
                if (paramDefinition && paramDefinition.premises && paramDefinition.premises.conditions.length > 0) {
                    const flatDsRow: Record<string, any> = { ...r };
                    if (r.data) {
                        Object.keys(r.data).forEach(k => flatDsRow[k] = r.data[k]?.value);
                    }
                    flatDsRow['meta_created_at'] = r.submittedAt || r.submitted_at || r.createdAt || r.created_at || r.startedAt || r.started_at || r.meta_created_at;
                    flatDsRow['meta_form_id'] = r.formId || r.form_id;
                    return evaluatePremises(paramDefinition.premises, flatDsRow);
                }
                return true;
            });

            const result = computeAggregationForDataset(aggType, fieldName, targetDataset);
            globalAggCache.set(cacheKey, result);
          }
        }
      }
    }

    // Traverse down
    if (calc.left) computeAggregations(calc.left, paramId);
    if (calc.right) computeAggregations(calc.right, paramId);
    if (calc.args) calc.args.forEach(a => computeAggregations(a, paramId));
  };

  // Run pass 1 for all parameters
  for (const param of parameters) {
    if (param.returnValue && typeof param.returnValue !== 'string') {
      computeAggregations(param.returnValue as Calculation, param.id);
    }
  }

  // PASSAGEM 2: Injeção Vetorial (Coluna a Coluna -> Linha a Linha)
  // Fazemos um clone profundo inicial
  const workingDataset = dataset.map(originalRow => {
    const row = { ...originalRow };
    if (!row.data) row.data = {};
    else row.data = { ...originalRow.data };

    // Injetar Global Aliases PRIMEIRO
    for (const alias of globalAliases) {
       if (alias.linkedFieldIds && alias.linkedFieldIds.length > 0) {
          let aliasValue: any = null;
          let aliasType = 'text';
          for (const fieldId of alias.linkedFieldIds) {
             let lReqFormId: string | null = null;
             let lReqFieldId = fieldId;
             if (fieldId.includes('::')) {
                 [lReqFormId, lReqFieldId] = fieldId.split('::');
             }
             if (lReqFormId && lReqFormId !== 'global') {
                 const rowFormId = row.formId || row.form_id;
                 if (rowFormId !== lReqFormId) continue;
             }
             
             if (row.data && row.data[lReqFieldId] !== undefined) {
                 aliasValue = row.data[lReqFieldId].value;
                 aliasType = row.data[lReqFieldId].type || 'text';
                 break;
             } else if (row[lReqFieldId] !== undefined) {
                 aliasValue = row[lReqFieldId];
                 aliasType = typeof aliasValue === 'number' ? 'number' : 'text';
                 break;
             }
          }
          row.data[alias.id] = { type: aliasType, value: aliasValue };
       }
    }

    // Injetar Global Inputs
    for (const input of globalInputs) {
       row.data[input.id] = { type: input.type || 'text', value: input.value };
    }
    
    // Preparar o flatRow cacheado na própria row para performance e reuso no filter
    const flatRow: Record<string, any> = { ...row };
    Object.keys(row.data).forEach(k => flatRow[k] = row.data[k]?.value);
    flatRow['meta_created_at'] = row.submittedAt || row.submitted_at || row.createdAt || row.created_at || row.startedAt || row.started_at || row.meta_created_at;
    flatRow['meta_lat_long'] = row.location ? `${row.location.lat},${row.location.lng}` : null;
    flatRow['meta_form_id'] = row.formId || row.form_id;
    
    row._flat = flatRow;
    return row;
  });

  const sortedParameters = sortContainersTopologically(parameters);
  const runningSums = new Map<string, number>();

  for (const param of sortedParameters) {
      let tForm = param.targetForm || (param as any)._metadata?.targetForm;
      if (tForm === 'form-2') tForm = 'form-1775766905782';
      if (tForm === 'form-1') tForm = 'form-1775762620276';

      for (const row of workingDataset) {
          const rowFormId = row.formId || row.form_id;
          if (tForm && tForm.toLowerCase() !== 'global' && rowFormId !== tForm) {
              continue;
          }

          const flatRow = row._flat;
          const meetsPremise = evaluatePremises(param.premises, flatRow);
          if (!meetsPremise) {
            row.data[param.id] = { type: 'number', value: param.fallbackValue !== undefined ? Number(param.fallbackValue) : null };
            flatRow[param.id] = row.data[param.id].value;
            continue;
          }

          const resolveCalc = (calc: Calculation, currentDatasetContext: any[] = workingDataset, currentFlatRowContext: Record<string, any> = flatRow, debugMode: boolean = false): any | null => {
            if (calc.type === "CONSTANT") {
                let val = calc.value;
                if (typeof val === 'string') {
                    if ((val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"')) || (val.startsWith('`') && val.endsWith('`'))) {
                        val = val.slice(1, -1);
                    }
                } else {
                    val = isNaN(Number(val)) ? val : Number(val);
                }
                return val;
            }

            if (calc.type === "FIELD") {
               const fieldName = String(calc.value);
               let reqFormId: string | null = null;
               let reqFieldId = fieldName;
               if (fieldName.includes('::')) {
                   [reqFormId, reqFieldId] = fieldName.split('::');
               }
               if (reqFormId && reqFormId.toLowerCase() !== 'global') {
                   const rFormId = currentFlatRowContext['meta_form_id'] || currentFlatRowContext['formId'] || currentFlatRowContext['form_id'];
                   if (rFormId !== reqFormId) return undefined;
               }
               let val = currentFlatRowContext[reqFieldId];
               if (val === undefined || val === null || val === "") return val;
               if (typeof val === 'string') {
                   if (val.length > 1 && val.startsWith('0') && !val.includes('.')) return val;
               }
               return isNaN(Number(val)) ? val : Number(val);
            }

            if (calc.type === "THIS_ROW") {
               const fieldName = String(calc.value);
               let reqFormId: string | null = null;
               let reqFieldId = fieldName;
               if (fieldName.includes('::')) {
                   [reqFormId, reqFieldId] = fieldName.split('::');
               }
               if (reqFormId && reqFormId.toLowerCase() !== 'global') {
                   const rFormId = flatRow['meta_form_id'] || flatRow['formId'] || flatRow['form_id'];
                   if (rFormId !== reqFormId) return undefined;
               }
               let val = flatRow[reqFieldId];
               if (val === undefined || val === null || val === "") return val;
               if (typeof val === 'string') {
                   if (val.length > 1 && val.startsWith('0') && !val.includes('.')) return val;
               }
               return isNaN(Number(val)) ? val : Number(val);
            }

            if (calc.type === "FUNCTION" && calc.value && calc.args && calc.args.length > 0) {
               const funcName = String(calc.value).toUpperCase();

               if (funcName === "SWITCH") {
                   const targetVal = resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext);
                   for(let i = 1; i < calc.args.length - 1; i += 2) {
                       const caseVal = resolveCalc(calc.args[i], currentDatasetContext, currentFlatRowContext);
                       const isTargetNumericStr = typeof targetVal === 'string' && targetVal.trim() !== '' && !isNaN(Number(targetVal));
                       const isCaseNumericStr = typeof caseVal === 'string' && caseVal.trim() !== '' && !isNaN(Number(caseVal));
                       const isTargetNum = typeof targetVal === 'number';
                       const isCaseNum = typeof caseVal === 'number';
                       const numericMatch = (isTargetNumericStr || isTargetNum) && (isCaseNumericStr || isCaseNum) && Number(targetVal) === Number(caseVal);
                       const strMatch = typeof targetVal === 'string' && typeof caseVal === 'string' && targetVal.trim().toUpperCase() === caseVal.trim().toUpperCase();
                       if (targetVal === caseVal || String(targetVal) === String(caseVal) || numericMatch || strMatch) {
                           return resolveCalc(calc.args[i+1], currentDatasetContext, currentFlatRowContext);
                       }
                   }
                   if (calc.args.length % 2 === 0) {
                       return resolveCalc(calc.args[calc.args.length - 1], currentDatasetContext, currentFlatRowContext);
                   }
                   return null;
               }

               if (funcName === "TIME") return extractTime(resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext));
               if (funcName === "DATE") return extractDate(resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext));
               
               if (funcName === "HORAS") {
                   const val = resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext);
                   if (isTimeString(val)) return timeToSeconds(val as string) / 3600;
                   return null;
               }
               if (funcName === "MINUTOS") {
                   const val = resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext);
                   if (isTimeString(val)) return timeToSeconds(val as string) / 60;
                   return null;
               }
               if (funcName === "LATLONG" && calc.args.length === 2) {
                   const lat = resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext);
                   const lng = resolveCalc(calc.args[1], currentDatasetContext, currentFlatRowContext);
                   if (lat !== null && lng !== null) return `${lat},${lng}`;
                   return null;
               }

               if (funcName === "PREVIOUS_RUNNING_SUM") {
                   const fieldCalc = calc.args[0];
                   if (fieldCalc && fieldCalc.type === "FIELD") {
                       const fieldName = String(fieldCalc.value);
                       const cacheKey = `${param.id}_RUNNING_${fieldName}`;
                       const prevSum = runningSums.get(cacheKey) || 0;
                       
                       let currentVal = resolveCalc(fieldCalc, currentDatasetContext, currentFlatRowContext);
                       currentVal = isNaN(Number(currentVal)) ? 0 : Number(currentVal);
                       
                       runningSums.set(cacheKey, prevSum + currentVal);
                       return prevSum;
                   }
                   return null;
               }

               if (funcName === "HOURS_TO_TIME") {
                   const val = resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext);
                   const hoursNum = Number(val);
                   if (isNaN(hoursNum)) return "00:00:00";
                   return secondsToTime(hoursNum * 3600);
               }

               if (funcName === "FILTER" && calc.args.length === 2) {
                   const conditionCalc = calc.args[0];
                   const aggregationCalc = calc.args[1];

                   let vtForm = param.targetForm || (param as any)._metadata?.targetForm;
                   if (vtForm === 'form-2') vtForm = 'form-1775766905782';
                   if (vtForm === 'form-1') vtForm = 'form-1775762620276';

                   const validContext = vtForm && vtForm !== 'global'
                      ? currentDatasetContext.filter(r => (r.formId || r.form_id) === vtForm)
                      : currentDatasetContext;

                   const filteredDataset = validContext.filter(dsRow => {
                       const fDsRow = dsRow._flat || dsRow;
                       const conditionResult = resolveCalc(conditionCalc, currentDatasetContext, fDsRow);
                       return conditionResult === 1;
                   });

                   return resolveCalc(aggregationCalc, filteredDataset, currentFlatRowContext);
               }

               if (funcName === "INDEX_OF" && calc.args.length === 2) {
                   const normalizeStr = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
                   const listStr = String(resolveCalc(calc.args[0], currentDatasetContext, currentFlatRowContext) || "");
                   const searchStr = String(resolveCalc(calc.args[1], currentDatasetContext, currentFlatRowContext) || "");
                   if (!listStr || !searchStr) return 999;
                   const normSearch = normalizeStr(searchStr);
                   const items = listStr.split(',').map(s => normalizeStr(s));
                   const idx = items.indexOf(normSearch);
                   if (idx !== -1) return idx + 1; // 1-indexed
                   return 999; // Not found, put at the end
               }

               const fieldCalc = calc.args[0];
               if (fieldCalc.type === "FIELD") {
                 const aggType = funcName;
                 if (currentDatasetContext !== workingDataset) {
                     return computeAggregationForDataset(aggType, String(fieldCalc.value), currentDatasetContext);
                 }
                 const cacheKey = `${param.id}_${aggType}_${String(fieldCalc.value)}`;
                 return globalAggCache.get(cacheKey) ?? null;
               }
               return null;
            }

            if (calc.type === "OPERATION" && calc.left && calc.right) {
               const leftVal = resolveCalc(calc.left, currentDatasetContext, currentFlatRowContext);
               const rightVal = resolveCalc(calc.right, currentDatasetContext, currentFlatRowContext);
               
               if (calc.operation === "OR" || calc.operation === "AND") {
                   const left = leftVal === null ? 0 : leftVal;
                   const right = rightVal === null ? 0 : rightVal;
                   if (calc.operation === "OR") return (left !== 0 || right !== 0) ? 1 : 0;
                   if (calc.operation === "AND") return (left !== 0 && right !== 0) ? 1 : 0;
               }

               if (leftVal === null || rightVal === null) return null;

               const isMathOp = ["ADD", "SUBTRACT", "MULTIPLY", "DIVIDE"].includes(calc.operation);
               if (isMathOp) {
                  console.log(`[DEBUG MathOp] op=${calc.operation} left=${leftVal} right=${rightVal} leftType=${typeof leftVal} rightType=${typeof rightVal} isTime(L)=${isTimeString(leftVal)} isTime(R)=${isTimeString(rightVal)}`);
                  if (!isTimeString(leftVal) && isNaN(Number(leftVal))) return "[DADO INVÁLIDO]";
                  if (!isTimeString(rightVal) && isNaN(Number(rightVal))) return "[DADO INVÁLIDO]";
               }

               switch (calc.operation) {
                 case "ADD": {
                     if (isTimeString(leftVal) && isTimeString(rightVal)) {
                         return secondsToTime(timeToSeconds(leftVal as string) + timeToSeconds(rightVal as string));
                     }
                     return Number(leftVal) + Number(rightVal);
                 }
                 case "SUBTRACT": {
                     if (isTimeString(leftVal) && isTimeString(rightVal)) {
                         return secondsToTime(timeToSeconds(leftVal as string) - timeToSeconds(rightVal as string));
                     }
                     return Number(leftVal) - Number(rightVal);
                 }
                 case "MULTIPLY": return Number(leftVal) * Number(rightVal);
                 case "DIVIDE":
                   if (Number(rightVal) === 0) return "[ERRO DE CÁLCULO]";
                   return Number(leftVal) / Number(rightVal);
                 case "EQUALS": {
                   const lStr = Array.isArray(leftVal) ? JSON.stringify(leftVal) : String(leftVal);
                   const rStr = Array.isArray(rightVal) ? JSON.stringify(rightVal) : String(rightVal);
                   if (leftVal === rightVal || lStr === rStr) return 1;
                   const isLeftNum = (typeof leftVal === 'number') || (typeof leftVal === 'string' && leftVal.trim() !== '' && !isNaN(Number(leftVal)));
                   const isRightNum = (typeof rightVal === 'number') || (typeof rightVal === 'string' && rightVal.trim() !== '' && !isNaN(Number(rightVal)));
                   if (isLeftNum && isRightNum && Number(leftVal) === Number(rightVal)) return 1;
                   return 0;
                 }
                 case "NOT_EQUALS": {
                   const lStr = Array.isArray(leftVal) ? JSON.stringify(leftVal) : String(leftVal);
                   const rStr = Array.isArray(rightVal) ? JSON.stringify(rightVal) : String(rightVal);
                   if (leftVal === rightVal || lStr === rStr) return 0;
                   const isLeftNum = (typeof leftVal === 'number') || (typeof leftVal === 'string' && leftVal.trim() !== '' && !isNaN(Number(leftVal)));
                   const isRightNum = (typeof rightVal === 'number') || (typeof rightVal === 'string' && rightVal.trim() !== '' && !isNaN(Number(rightVal)));
                   if (isLeftNum && isRightNum && Number(leftVal) === Number(rightVal)) return 0;
                   return 1;
                 }
                 case "GREATER_THAN": return leftVal > rightVal ? 1 : 0;
                 case "LESS_THAN": return leftVal < rightVal ? 1 : 0;
                 case "GREATER_THAN_EQUALS": return leftVal >= rightVal ? 1 : 0;
                 case "LESS_THAN_EQUALS": return leftVal <= rightVal ? 1 : 0;
                 case "AND": return (leftVal !== 0 && rightVal !== 0) ? 1 : 0;
                 case "OR": return (leftVal !== 0 || rightVal !== 0) ? 1 : 0;
                 default: return null;
               }
            }
            return null;
          };

          let finalValue: any | null = null;
          if (param.returnValue.type === "CONSTANT") {
             const val = param.returnValue.value;
             if (typeof val === 'string' && val.startsWith("'") && val.endsWith("'")) {
                 finalValue = val.slice(1, -1);
             } else {
                 finalValue = isNaN(Number(val)) ? val : Number(val);
             }
          } else if (param.returnValue.type === "FIELD") {
             const fieldName = String(param.returnValue.value);
             let reqFormId: string | null = null;
             let reqFieldId = fieldName;
             if (fieldName.includes('::')) {
                 [reqFormId, reqFieldId] = fieldName.split('::');
             }
             const val = flatRow[reqFieldId];
             finalValue = (val === undefined || val === null || val === "") ? null : Number(val);
          } else {
             finalValue = resolveCalc(param.returnValue as Calculation);
          }

          if (finalValue === null && param.fallbackValue !== undefined && param.fallbackValue !== "") {
             finalValue = Number(param.fallbackValue);
          }

          row.data[param.id] = { type: 'number', value: finalValue };
          flatRow[param.id] = finalValue;
          row[`c_${param.id}`] = finalValue;
          flatRow[`c_${param.id}`] = finalValue;
      }
  }

  // Limpar a propriedade _flat usada temporariamente
  return workingDataset.map(r => {
      const cleanRow = { ...r };
      delete cleanRow._flat;
      return cleanRow;
  });
}
